import { ConfirmEntry, ConfirmRate, ConfirmRoom, ConfirmStat, ConfirmSummary } from '~/types/availability';
import { ReopenExtraction, ReopenImpact, ReopenManualRoom, ReopenManualRoomDate } from '~/types/reopen';
import { CatalogRoom, ExtractedDateRange } from '~/types/stopsell';

import { formatDateRange } from '~/utils/dateRangeUtils';

export function buildReopenConfirmSummary(
  extraction: ReopenExtraction,
  localDates: Array<Array<ExtractedDateRange>>,
  impacts: Array<Array<Array<ReopenImpact>>>,
  deselectedBlockIds: Set<string>,
  catalogRooms: Array<CatalogRoom>,
  roomTypeOverrides: Record<number, string>,
  manualRooms?: Array<ReopenManualRoom>,
): ConfirmSummary {
  const rooms: Array<ConfirmRoom> = [];

  const stats = {
    rooms: 0,
    rates: new Set<string>(),
    deleted: 0,
    recreated: 0,
    ignored: 0,
  };

  function buildRoomRates(
    dates: Array<ExtractedDateRange | ReopenManualRoomDate>,
    catalogRoom: CatalogRoom,
    impactRows?: Array<Array<ReopenImpact>>,
  ): Array<ConfirmRate> {
    const rateEntries: Map<string, Array<ConfirmEntry>> = new Map();
    const seenBlockIds = new Set<string>();

    dates.forEach((date, dateIndex) => {
      if (!date.selected) return;

      const dateImpacts = 'impacts' in date ? date.impacts : (impactRows?.[dateIndex] ?? []);
      if (dateImpacts.length === 0) return;

      dateImpacts.forEach((impact) => {
        impact.blackout.blocks.forEach((block) => {
          if (seenBlockIds.has(block.id)) return;
          seenBlockIds.add(block.id);
          const rateId = block.room_rate_id;
          const rate = catalogRoom.roomRates?.find((r) => r.id === rateId);
          if (!rate) return;

          if (!rateEntries.has(rateId)) {
            rateEntries.set(rateId, []);
          }

          const blockIsDeselected = deselectedBlockIds.has(block.id);
          if (!blockIsDeselected) {
            const remainderDetail =
              impact.remainders.length > 0
                ? `removes ${formatDateRange(impact.blackout.start, impact.blackout.end)}, recreates ${impact.remainders
                    .map((r) => formatDateRange(r.start, r.end))
                    .join(', ')}`
                : undefined;
            rateEntries.get(rateId)!.push({
              start: date.start,
              end: date.end,
              action: 'delete',
              detail: remainderDetail,
            });
            stats.deleted += 1;
            stats.recreated += impact.remainders.length;
          } else {
            rateEntries.get(rateId)!.push({
              start: date.start,
              end: date.end,
              action: 'ignored',
            });
            stats.ignored += 1;
          }
        });
      });
    });

    const confirmRates: Array<ConfirmRate> = [];
    catalogRoom.roomRates?.forEach((rate) => {
      const entries = rateEntries.get(rate.id) ?? [];
      if (entries.length === 0) return;

      stats.rates.add(rate.name);
      confirmRates.push({
        rateName: rate.name,
        entries,
      });
    });

    return confirmRates;
  }

  extraction.rooms.forEach((room, roomIndex) => {
    const dates = localDates[roomIndex] ?? [];
    if (!dates.some((d, dateIndex) => d.selected && (impacts[roomIndex]?.[dateIndex]?.length ?? 0) > 0)) return;

    const roomTypeId = roomTypeOverrides[roomIndex] ?? room.roomTypeId;
    if (!roomTypeId) return;

    const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
    if (!catalogRoom) return;

    const rates = buildRoomRates(dates, catalogRoom, impacts[roomIndex]);
    if (rates.length === 0) return;

    stats.rooms += 1;
    rooms.push({
      roomName: room.name,
      catalogRoomName: catalogRoom.name,
      rates,
    });
  });

  if (manualRooms) {
    manualRooms.forEach((room) => {
      const dates = room.dates.filter((d) => d.selected && d.impacts.length > 0);
      if (dates.length === 0) return;

      const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
      if (!catalogRoom) return;

      const rates = buildRoomRates(dates, catalogRoom);
      if (rates.length === 0) return;

      stats.rooms += 1;
      rooms.push({
        roomName: room.catalogRoomName,
        catalogRoomName: catalogRoom.name,
        rates,
      });
    });
  }

  const confirmStats: Array<ConfirmStat> = [
    { label: 'ROOMS', value: stats.rooms },
    { label: 'RATES', value: stats.rates.size },
    { label: 'BLOCKS DELETED', value: stats.deleted, color: 'warning' },
    { label: 'RANGES RECREATED', value: stats.recreated, color: 'success' },
    { label: 'IGNORED', value: stats.ignored },
  ];

  return {
    rooms,
    stats: confirmStats,
  };
}
